import { useEffect, useMemo, useRef, useState } from 'react';
import { LegalTimelineModal, StatusChip, TimelineButton } from './legalShared';
import { ManageCategoriesModal } from './ManageCategoriesModal';
import { ManageLegalApiModal } from './ManageLegalApiModal';
import { COUNTRY_OPTIONS } from './countryList';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

const LEGAL_PAGE_SIZE = 10;

const LEGAL_COLS = [
    { key: 'entity_name',               label: 'Entity Name' },
    { key: 'country',                   label: 'Country' },
    { key: 'requestor_name',            label: 'Requestor' },
    { key: 'document_category',         label: 'Document Category' },
    { key: 'document_subcategory',      label: 'Document Sub-Category' },
    { key: 'received_on',               label: 'Received On' },
    { key: 'legal_status',              label: 'Legal Status', status: true },
    { key: 'finance_status',            label: 'Finance Status', status: true },
    { key: 'kosmos_signatory_status',   label: '1Kosmos Sig.', status: true },
    { key: 'partner_signatory_status',  label: 'Partner Sig.', status: true },
    { key: 'current_status',            label: 'Current Status', status: true },
];

// One reference chip per status color, shown as a legend in the toolbar so
// the color-coding in the table below is self-explanatory.
const STATUS_LEGEND = [
    { label: 'Completed', className: 'legal-status-completed' },
    { label: 'In Progress', className: 'legal-status-progress' },
    { label: 'On Hold', className: 'legal-status-hold' },
    { label: 'Pending', className: 'legal-status-pending' },
    { label: 'Cancelled', className: 'legal-status-cancelled' },
    { label: 'Signed copy awaited', className: 'legal-status-awaited' },
    { label: 'NA', className: 'legal-status-na' },
];

const EMPTY_FORM = {
    entity_name: '', country: '', requestor_name: '', finance_poc: '',
    document_category: '', document_subcategory: '',
    received_on: '', legal_status: '', finance_status: '', kosmos_signatory_status: '',
    partner_signatory_status: '', current_status: '', saved_on_relativity: '',
    current_status_comments: '', additional_comments: '',
};

// ── ConfirmModal — generic Yes/No confirmation card, used in place of
// window.confirm wherever Manage Legal Master needs an irreversible-feeling
// action (deleting a record, purging it from the Bin) confirmed explicitly. ──

function ConfirmModal({ icon = '⚠️', title, message, confirmLabel = 'Yes', cancelLabel = 'No', danger = false, busy = false, onConfirm, onCancel }) {
    return (
        <div className="legal-modal-bg" style={{ zIndex: 900 }} onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
            <div className="legal-modal legal-confirm-modal">
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">{icon}</div>
                    <div className="legal-modal-title">{title}</div>
                    <button className="legal-modal-close" disabled={busy} onClick={onCancel} style={{ marginLeft: 'auto' }}>✕</button>
                </div>
                <div className="legal-modal-body">
                    <p className="legal-confirm-message">{message}</p>
                </div>
                <div className="legal-modal-footer">
                    <button className="legal-btn-cancel" disabled={busy} onClick={onCancel}>{cancelLabel}</button>
                    <button className={danger ? 'legal-btn-danger' : 'legal-btn-save'} disabled={busy} onClick={onConfirm}>
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── LegalUploadModeModal ─────────────────────────────────────────────────────

function LegalUploadModeModal({ onClose, onProceed, uploading }) {
    const [mode, setMode] = useState('replace');
    return (
        <div className="bim-backdrop" style={{ zIndex: 1200 }} onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}>
            <div className="bim-card" style={{ maxWidth: 480 }}>
                <div className="bim-header">
                    <div className="bim-header-left">
                        <div className="bim-header-icon">⚙️</div>
                        <div>
                            <div className="bim-title">Choose Import Mode</div>
                            <div className="bim-subtitle">How should the uploaded data be applied?</div>
                        </div>
                    </div>
                    <button className="bim-close" disabled={uploading} onClick={onClose}>✕</button>
                </div>
                <div className="bim-body" style={{ gap: 12 }}>
                    <label className={`upm-option${mode === 'replace' ? ' upm-option--active' : ''}`} onClick={() => setMode('replace')}>
                        <div className="upm-radio"><div className={`upm-radio-dot${mode === 'replace' ? ' upm-radio-dot--on' : ''}`} /></div>
                        <div className="upm-option-body">
                            <div className="upm-option-title">Replace</div>
                            <div className="upm-option-desc">Delete all existing Legal records and re-import fresh from this file.</div>
                        </div>
                    </label>
                    <label className={`upm-option${mode === 'insert' ? ' upm-option--active' : ''}`} onClick={() => setMode('insert')}>
                        <div className="upm-radio"><div className={`upm-radio-dot${mode === 'insert' ? ' upm-radio-dot--on' : ''}`} /></div>
                        <div className="upm-option-body">
                            <div className="upm-option-title">Insert</div>
                            <div className="upm-option-desc">Append new rows to the existing Legal records without deleting anything.</div>
                        </div>
                    </label>
                </div>
                <div className="bim-footer">
                    <button className="bim-cancel-btn" disabled={uploading} onClick={onClose}>Back</button>
                    <button className="bim-upload-btn" disabled={uploading} onClick={() => onProceed(mode)}>
                        {uploading ? <><span className="bim-spinner" />Uploading…</> : <>↑ &nbsp;Proceed to Submit</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── LegalImportModal ─────────────────────────────────────────────────────────

function LegalImportModal({ onClose, onProceed, uploading }) {
    const [file, setFile] = useState(null);
    const [showModeModal, setShowModeModal] = useState(false);
    const fileRef = useRef();

    const downloadTemplate = () => {
        const headers = [
            'S.no', 'Entity Name', 'Country', 'Requestor Name', 'Finance Point of Contact',
            'Document Category', 'Document Sub-Category', 'Received On', 'Legal Status', 'Finance Status',
            '1kosmos Signatory Status', 'Partner Signatory Status', 'Current Status',
            'Saved on Relativity', 'Current Status Comments', 'Additional Comments',
        ];
        const sample = [
            '1', 'Sample Corp', 'US', 'Jane Doe', 'Georgiana/Michael', 'Confidentiality & Privacy', 'NDA',
            '2026-01-05', 'Completed', 'Completed', 'Completed', 'Completed',
            'Completed', 'Completed', '', '',
        ];
        const csv = `${headers.map(h => `"${h}"`).join(',')}\n${sample.map(v => `"${v}"`).join(',')}\n`;
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        Object.assign(document.createElement('a'), { href: url, download: 'legal_dashboard_template.csv' }).click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            {showModeModal && (
                <LegalUploadModeModal
                    uploading={uploading}
                    onClose={() => setShowModeModal(false)}
                    onProceed={(mode) => { setShowModeModal(false); onProceed(file, mode); }}
                />
            )}
            <div className="bim-backdrop" onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}>
                <div className="bim-card">
                    <div className="bim-header">
                        <div className="bim-header-left">
                            <div className="bim-header-icon">📥</div>
                            <div>
                                <div className="bim-title">Import Legal Dashboard Excel</div>
                                <div className="bim-subtitle">Prepare your file using the instructions below, then upload</div>
                            </div>
                        </div>
                        <button className="bim-close" onClick={onClose} disabled={uploading}>✕</button>
                    </div>
                    <div className="bim-body">
                        <div className="bim-step">
                            <div className="bim-step-badge">1</div>
                            <div className="bim-step-body">
                                <div className="bim-step-title">File Preparation Instructions</div>
                                <ul className="bim-instructions">
                                    <li>Upload an <strong>Excel (.xlsx)</strong> file only. The file may have multiple sheets (e.g. pivot/summary tabs) — the sheet whose header row contains the recognizable column names is picked automatically; column order within that sheet doesn't matter.</li>
                                    <li>Required columns: <strong>Entity Name</strong> and <strong>Document Category</strong> (a plain "Type of document" column also works). All other columns (Country, Requestor Name, Finance Point of Contact, <strong>Document Sub-Category</strong>, Received On, Legal/Finance/Signatory Status, Current Status, Saved on Relativity, Comments) are optional.</li>
                                    <li><strong>Replace mode</strong> deletes all existing Legal records and reloads everything fresh from this file. <strong>Insert mode</strong> appends new rows without deleting anything.</li>
                                    <li><strong>Received On</strong> should use a standard Excel date cell or <code>YYYY-MM-DD</code> format.</li>
                                </ul>
                            </div>
                        </div>
                        <div className="bim-step">
                            <div className="bim-step-badge bim-step-badge-green">2</div>
                            <div className="bim-step-body">
                                <div className="bim-step-title">Download Sample Template</div>
                                <div className="bim-step-desc">A CSV with the recognised column headers and one example row.</div>
                                <div className="bim-sample-row">
                                    <button className="bim-sample-btn bim-sample-csv" onClick={downloadTemplate}>↓ &nbsp;Template (.csv)</button>
                                </div>
                            </div>
                        </div>
                        <div className="bim-step">
                            <div className="bim-step-badge bim-step-badge-purple">3</div>
                            <div className="bim-step-body">
                                <div className="bim-step-title">Select & Upload Your File</div>
                                <div className="bim-step-desc">Accepts <code>.xlsx</code> format only.</div>
                                <div className="bim-file-row">
                                    <label className="bim-choose-btn" onClick={() => fileRef.current?.click()}>Choose File</label>
                                    <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => setFile(e.target.files?.[0] || null)} />
                                    <span className={`bim-file-name ${file ? 'bim-file-selected' : ''}`}>{file ? `📄 ${file.name}` : 'No file selected'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bim-footer">
                        <button className="bim-cancel-btn" onClick={onClose} disabled={uploading}>Cancel</button>
                        <button className="bim-upload-btn" onClick={() => setShowModeModal(true)} disabled={!file || uploading}>
                            {uploading ? <><span className="bim-spinner" />Uploading…</> : <>↑ &nbsp;Upload Now</>}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

// ── SuggestInput ─────────────────────────────────────────────────────────────
// Free-text input (any value is accepted, same as before this was added) that
// also offers whatever values already exist for this same column across
// every legal record, so a repeat Requestor Name / Finance Point of Contact
// doesn't need to be retyped character-for-character. Typing filters the
// list to substring matches; clicking a suggestion pastes it in verbatim.
// `onMouseDown` (not `onClick`) on the suggestion button fires the selection
// before the input's own blur closes the list, so the click isn't swallowed.
function SuggestInput({ value, onChange, options, placeholder }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        const onDocMouseDown = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, []);

    const query = value.trim().toLowerCase();
    const matches = (query
        ? options.filter(o => o.toLowerCase().includes(query) && o.toLowerCase() !== query)
        : options
    ).slice(0, 8); // capped so a column with hundreds of distinct names doesn't dump an unscrollable wall under one keystroke

    return (
        <div className="legal-suggest" ref={wrapRef}>
            <input
                value={value}
                placeholder={placeholder}
                onChange={e => { onChange(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
            />
            {open && !!matches.length && (
                <ul className="legal-suggest-list">
                    {matches.map(o => (
                        <li key={o}>
                            <button type="button" onMouseDown={e => { e.preventDefault(); onChange(o); setOpen(false); }}>{o}</button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ── LegalRecordModal (Add/Edit) ──────────────────────────────────────────────

function LegalRecordModal({ record, records, onClose, onSaved, save }) {
    const [form, setForm] = useState(() => record ? {
        entity_name: record.entity_name || '', country: record.country || '',
        requestor_name: record.requestor_name || '', finance_poc: record.finance_poc || '',
        document_category: record.document_category || '', document_subcategory: record.document_subcategory || '',
        received_on: record.received_on || '',
        legal_status: record.legal_status || '', finance_status: record.finance_status || '',
        kosmos_signatory_status: record.kosmos_signatory_status || '',
        partner_signatory_status: record.partner_signatory_status || '',
        current_status: record.current_status || '', saved_on_relativity: record.saved_on_relativity || '',
        current_status_comments: record.current_status_comments || '', additional_comments: record.additional_comments || '',
    } : EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // Every record created via "+ Add Another" this session, newest first —
    // kept as a running list (not a single banner that the next keystroke
    // wipes out) so a user batching in several records can still see what
    // they already added a few entries back.
    const [addedThisSession, setAddedThisSession] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_URL}/api/legal/categories/`, { credentials: 'include' });
                const body = await readApiJson(res, 'Could not load categories.');
                if (!cancelled) setCategories(body.categories || []);
            } catch {
                // Category/Sub-Category become plain disabled selects below if this fails.
            } finally {
                if (!cancelled) setCategoriesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
    const setField = (field) => (v) => setForm(f => ({ ...f, [field]: v }));

    // Every already-used Requestor Name / Finance POC across every legal
    // record, for SuggestInput's click-to-fill dropdown — a different name on
    // almost every record is still just one distinct value each, so this
    // stays cheap even as the table grows.
    const requestorOptions = useMemo(
        () => Array.from(new Set((records || []).map(r => r.requestor_name).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
        [records],
    );
    const financePocOptions = useMemo(
        () => Array.from(new Set((records || []).map(r => r.finance_poc).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
        [records],
    );

    // Sub-Category options depend on the selected Category — switching
    // Category clears Sub-Category unless the current value still belongs
    // to the newly-selected one (keeps an edit's existing pairing intact).
    const selectedCategory = categories.find(c => c.name === form.document_category);
    const subcategoryOptions = selectedCategory?.subcategories || [];
    const setCategory = (e) => {
        const name = e.target.value;
        const cat = categories.find(c => c.name === name);
        setForm(f => ({
            ...f,
            document_category: name,
            document_subcategory: cat?.subcategories.some(s => s.name === f.document_subcategory) ? f.document_subcategory : '',
        }));
    };

    const submit = async () => {
        if (!form.entity_name.trim() || !form.document_category.trim()) {
            setError('Entity Name and Document Category are required.');
            return;
        }
        setSaving(true); setError('');
        try {
            const payload = record ? { id: record.id, ...form } : { ...form };
            await save(payload);
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Add-mode only — creates the record but deliberately leaves the form
    // filled in (instead of closing like submit()/onSaved() does), so a user
    // entering a batch of similar records doesn't have to retype the fields
    // that stay the same between them.
    const saveAndAddAnother = async () => {
        if (!form.entity_name.trim() || !form.document_category.trim()) {
            setError('Entity Name and Document Category are required.');
            return;
        }
        setSaving(true); setError('');
        try {
            await save({ ...form });
            setAddedThisSession(list => [
                { name: form.entity_name, category: form.document_category },
                ...list,
            ]);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
            <div className="legal-modal legal-modal-wide">
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">{record ? '✏️' : '➕'}</div>
                    <div>
                        <div className="legal-modal-title">{record ? 'Edit Legal Record' : 'Add Legal Record'}</div>
                        <div className="legal-modal-sub">{record ? `Editing ${record.entity_name || 'record'}` : 'Create a new document tracking row'}</div>
                    </div>
                    <button className="legal-modal-close" onClick={onClose} disabled={saving}>✕</button>
                </div>
                <div className="legal-modal-body">
                    {error && <div className="legal-form-error">{error}</div>}
                    {addedThisSession.length > 0 && (
                        <div className="legal-form-notice legal-added-session">
                            <div className="legal-added-session-hdr">
                                ✓ {addedThisSession.length} record{addedThisSession.length > 1 ? 's' : ''} added this session — form left as-is, change what's different and add the next one
                            </div>
                            <ul className="legal-added-session-list">
                                {addedThisSession.map((item, i) => (
                                    <li key={i}>{item.name}{item.category ? ` — ${item.category}` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="legal-form-section">
                        <div className="legal-form-section-title"><span className="legal-form-section-icon">📄</span>Document Details</div>
                        <div className="legal-form-grid">
                            <div className="legal-field"><label>Entity Name <span className="legal-required">*</span></label><input value={form.entity_name} onChange={set('entity_name')} placeholder="e.g. Aranca" /></div>
                            <div className="legal-field">
                                <label>Document Category <span className="legal-required">*</span></label>
                                <select value={form.document_category} onChange={setCategory} disabled={categoriesLoading}>
                                    <option value="">{categoriesLoading ? 'Loading…' : 'Select a category'}</option>
                                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="legal-field">
                                <label>Document Sub-Category</label>
                                <select value={form.document_subcategory} onChange={set('document_subcategory')} disabled={!selectedCategory || !subcategoryOptions.length}>
                                    <option value="">{selectedCategory ? (subcategoryOptions.length ? 'Select a sub-category' : 'No sub-categories yet') : 'Choose a category first'}</option>
                                    {subcategoryOptions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="legal-field">
                                <label>Country</label>
                                <select value={form.country} onChange={set('country')}>
                                    <option value="">Select a country</option>
                                    {/* A legacy/free-text value not in the standard list (e.g. from an old
                                        Excel import) still shows up here instead of silently disappearing. */}
                                    {form.country && !COUNTRY_OPTIONS.includes(form.country) && (
                                        <option value={form.country}>{form.country}</option>
                                    )}
                                    {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="legal-field"><label>Received On</label><input type="date" value={form.received_on} onChange={set('received_on')} /></div>
                        </div>
                    </div>

                    <div className="legal-form-section">
                        <div className="legal-form-section-title"><span className="legal-form-section-icon">👤</span>Contacts</div>
                        <div className="legal-form-grid">
                            <div className="legal-field"><label>Requestor Name</label><SuggestInput value={form.requestor_name} onChange={setField('requestor_name')} options={requestorOptions} placeholder="Type a name…" /></div>
                            <div className="legal-field"><label>Finance Point of Contact</label><SuggestInput value={form.finance_poc} onChange={setField('finance_poc')} options={financePocOptions} placeholder="Type a name…" /></div>
                        </div>
                    </div>

                    <div className="legal-form-section">
                        <div className="legal-form-section-title"><span className="legal-form-section-icon">🚦</span>Status Tracking</div>
                        {record ? (
                            <>
                                <div className="legal-form-grid">
                                    <div className="legal-field"><label>Legal Status</label><StatusChip value={form.legal_status} /></div>
                                    <div className="legal-field"><label>Finance Status</label><StatusChip value={form.finance_status} /></div>
                                    <div className="legal-field"><label>1Kosmos Signatory Status</label><StatusChip value={form.kosmos_signatory_status} /></div>
                                    <div className="legal-field"><label>Partner Signatory Status</label><StatusChip value={form.partner_signatory_status} /></div>
                                    <div className="legal-field"><label>Current Status</label><StatusChip value={form.current_status} /></div>
                                    <div className="legal-field"><label>Saved on Relativity</label><StatusChip value={form.saved_on_relativity} /></div>
                                </div>
                                <div className="legal-status-pending-note">
                                    Statuses are read-only here — close this and use the 🕘 Timeline button on the row to change a status with an effective date and comment.
                                </div>
                            </>
                        ) : (
                            <p style={{ color: '#65676b', fontSize: 12.5 }}>Save this record first, then use its 🕘 Timeline button to set statuses with an effective date.</p>
                        )}
                    </div>

                    <div className="legal-form-section">
                        <div className="legal-form-section-title"><span className="legal-form-section-icon">📝</span>Notes</div>
                        <div className="legal-form-grid legal-form-full">
                            <div className="legal-field"><label>Current Status Comments</label><textarea value={form.current_status_comments} onChange={set('current_status_comments')} placeholder="Optional — context on where this stands" /></div>
                            <div className="legal-field"><label>Additional Comments</label><textarea value={form.additional_comments} onChange={set('additional_comments')} placeholder="Optional — anything else worth noting" /></div>
                        </div>
                    </div>
                </div>
                <div className="legal-modal-footer">
                    <button className="legal-btn-cancel" onClick={onClose} disabled={saving}>{record ? 'Cancel' : 'Close'}</button>
                    {!record && (
                        <button className="legal-btn-save-another" onClick={saveAndAddAnother} disabled={saving}>
                            {saving ? 'Saving…' : '+ Add Another'}
                        </button>
                    )}
                    <button className="legal-btn-save" onClick={submit} disabled={saving}>{saving ? 'Saving…' : record ? 'Save Changes' : 'Add Record'}</button>
                </div>
            </div>
        </div>
    );
}

// ── LegalBinModal — "Delete" on a record doesn't remove it outright; it
// moves the record (and a snapshot of its status history) into LegalRecordBin
// (see legal_views/bin.py), and this is where it waits until a data manager
// restores it to Manage Legal Master or purges it for good. ─────────────────

function LegalBinRow({ row, onRestore, onPurge, busy }) {
    return (
        <tr className="bm-row">
            <td className="bm-td-primary">{row.entity_name || '—'}</td>
            <td><span className="bm-chip">{row.country || '—'}</span></td>
            <td className="bm-td-muted">{row.document_category || '—'}</td>
            <td className="bm-td-muted">{row.deleted_by || '—'}</td>
            <td className="bm-td-muted">{row.deleted_at ? new Date(row.deleted_at).toLocaleString() : '—'}</td>
            <td>
                <div className="legal-row-actions">
                    <button className="legal-action-btn" title="Restore to Manage Legal Master" disabled={!!busy} onClick={() => onRestore(row)}>
                        {busy === 'restore' ? '…' : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                        )}
                    </button>
                    <button className="legal-action-btn legal-action-danger" title="Delete permanently" disabled={!!busy} onClick={() => onPurge(row)}>
                        {busy === 'purge' ? '…' : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        )}
                    </button>
                </div>
            </td>
        </tr>
    );
}

function LegalBinModal({ onClose, onRestored }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionBusy, setActionBusy] = useState(null); // { id, action }
    const [confirmPurge, setConfirmPurge] = useState(null);

    const load = async () => {
        setLoading(true); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/bin/`, { credentials: 'include' });
            const body = await readApiJson(res, 'Could not load the Bin.');
            setRows(body.records || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const restore = async (row) => {
        setActionBusy({ id: row.id, action: 'restore' }); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/bin/restore/`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ id: row.id }),
            });
            await readApiJson(res, 'Could not restore the record.');
            await load();
            onRestored?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setActionBusy(null);
        }
    };

    const purge = async (row) => {
        setActionBusy({ id: row.id, action: 'purge' }); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/bin/purge/`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ id: row.id }),
            });
            await readApiJson(res, 'Could not permanently delete the record.');
            setConfirmPurge(null);
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setActionBusy(null);
        }
    };

    return (
        <>
            <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="legal-modal legal-modal-wide">
                    <div className="legal-modal-hdr">
                        <div className="legal-modal-icon">🗑️</div>
                        <div>
                            <div className="legal-modal-title">Recycle Bin</div>
                            <div className="legal-modal-sub">{rows.length} deleted record{rows.length === 1 ? '' : 's'} — restore to Manage Legal Master or delete permanently</div>
                        </div>
                        <button className="legal-modal-close" onClick={onClose} style={{ marginLeft: 'auto' }}>✕</button>
                    </div>
                    <div className="legal-modal-body">
                        {error && <div className="legal-form-error">{error}</div>}
                        {loading ? <p style={{ color: '#65676b' }}>Loading…</p> : (
                            <table className="bm-table">
                                <thead>
                                    <tr>
                                        <th className="bm-th">Entity Name</th>
                                        <th className="bm-th">Country</th>
                                        <th className="bm-th">Document Category</th>
                                        <th className="bm-th">Deleted By</th>
                                        <th className="bm-th">Deleted At</th>
                                        <th className="bm-th">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <LegalBinRow
                                            key={row.id} row={row}
                                            onRestore={restore}
                                            onPurge={r => setConfirmPurge(r)}
                                            busy={actionBusy?.id === row.id ? actionBusy.action : null}
                                        />
                                    ))}
                                    {rows.length === 0 && (
                                        <tr><td colSpan={6} className="bm-empty-row">The bin is empty.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
            {confirmPurge && (
                <ConfirmModal
                    icon="⚠️"
                    title="Delete Permanently?"
                    message={`Permanently delete the record for "${confirmPurge.entity_name || 'this entry'}"? This cannot be undone — it will no longer be in the Bin.`}
                    confirmLabel="Yes, Delete Permanently"
                    danger
                    busy={actionBusy?.id === confirmPurge.id && actionBusy.action === 'purge'}
                    onConfirm={() => purge(confirmPurge)}
                    onCancel={() => setConfirmPurge(null)}
                />
            )}
        </>
    );
}

// ── LegalTable — shared row rendering for both the paginated table and the
// expanded full-screen view, so the two never drift out of sync ──────────────

function LegalTable({ rows, offset = 0, sortField, sortDir, onSort, onEdit, onDelete, onTimeline, deleteBusy }) {
    const Th = ({ col }) => (
        <th className="bm-th" onClick={() => onSort(col.key)}>
            {col.label}
            <span className="bm-sort-icon">{sortField === col.key ? (sortDir > 0 ? '↑' : '↓') : '↕'}</span>
        </th>
    );
    return (
        <table className="bm-table">
            <thead>
                <tr>
                    <th className="bm-th bm-th-num">#</th>
                    {LEGAL_COLS.map(col => <Th key={col.key} col={col} />)}
                    <th className="bm-th">Actions</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={r.id || i} className="bm-row">
                        <td className="bm-td-num">{offset + i + 1}</td>
                        <td className="bm-td-primary">{r.entity_name || '—'}</td>
                        <td><span className="bm-chip">{r.country || '—'}</span></td>
                        <td className="bm-td-muted">{r.requestor_name || '—'}</td>
                        <td className="bm-td-muted">{r.document_category || '—'}</td>
                        <td className="bm-td-muted">{r.document_subcategory || '—'}</td>
                        <td className="bm-td-muted">{r.received_on || '—'}</td>
                        <td><StatusChip value={r.legal_status} /></td>
                        <td><StatusChip value={r.finance_status} /></td>
                        <td><StatusChip value={r.kosmos_signatory_status} /></td>
                        <td><StatusChip value={r.partner_signatory_status} /></td>
                        <td><StatusChip value={r.current_status} /></td>
                        <td>
                            <div className="legal-row-actions">
                                <TimelineButton onClick={() => onTimeline(r)} />
                                <button className="legal-action-btn" title="Edit" onClick={() => onEdit(r)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                </button>
                                <button
                                    className="legal-action-btn legal-action-danger"
                                    title="Delete"
                                    disabled={deleteBusy === r.id}
                                    onClick={() => onDelete(r)}
                                >
                                    {deleteBusy === r.id ? '…' : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                    )}
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
                {rows.length === 0 && (
                    <tr><td colSpan={LEGAL_COLS.length + 2} className="bm-empty-row">No matching records.</td></tr>
                )}
            </tbody>
        </table>
    );
}

// ── LegalExpandedTable — full-screen view of every filtered record, no
// pagination, header fixed in place while only the body scrolls ─────────────

function LegalExpandedTable({
    rows, sortField, sortDir, onSort, onEdit, onDelete, onTimeline, deleteBusy, onClose,
    search, onSearchChange, onExport, onAdd, onImport, onManageCategories, onManageApi, onManageBin, uploading,
}) {
    return (
        <div className="legal-expand-overlay">
            <div className="legal-expand-hdr">
                {/* <span className="legal-expand-title">Manage Legal</span> */}
                {/* <span className="bm-badge">{rows.length} records</span> */}
                <input
                    className="bm-search"
                    placeholder="Search legal records…"
                    value={search}
                    onChange={e => onSearchChange(e.target.value)}
                />
                <div className="legal-status-legend" title="What each status color means">
                    {STATUS_LEGEND.map(({ label, className }) => (
                        <span className="legal-status-legend-item" key={label}>
                            <span className={`legal-status-chip ${className}`}>{label}</span>
                        </span>
                    ))}
                </div>
                <div className="bm-toolbar-right">
                    {/* <button className="bm-export-btn" title="Download the filtered table as CSV" onClick={onExport}>↓ Export CSV</button> */}
                    <button className="bm-export-btn" onClick={onAdd}>+ Add Record</button>
                    <button className={`bm-import-btn${uploading ? ' bm-import-loading' : ''}`} disabled={uploading} onClick={onImport}>
                        {uploading ? 'Importing…' : '↑ Import Legal Excel'}
                    </button>
                    <button className="bm-export-btn" onClick={onManageCategories}>🗂️ Manage Categories</button>
                    <button className="bm-export-btn" title="Let an external system push records in as JSON" onClick={onManageApi}>🔌 API</button>
                    <button className="bm-export-btn" title="View deleted records" onClick={onManageBin}>🗑️ Bin</button>
                </div>
                <button className="legal-modal-close" onClick={onClose} title="Close" style={{ marginLeft: 'auto' }}>✕</button>
            </div>
            <div className="legal-expand-body">
                <LegalTable
                    rows={rows}
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={onSort}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onTimeline={onTimeline}
                    deleteBusy={deleteBusy}
                />
            </div>
        </div>
    );
}

// ── ManageMISMaster ──────────────────────────────────────────────────────────

function ManageLegal({ data, upload, save, uploading, canManageData = false, onCategoriesChanged, onStatusChanged }) {
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('entity_name');
    const [sortDir, setSortDir] = useState(1);
    const [page, setPage] = useState(1);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [timelineRecord, setTimelineRecord] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showExpanded, setShowExpanded] = useState(false);
    const [showCategoriesModal, setShowCategoriesModal] = useState(false);
    const [showApiModal, setShowApiModal] = useState(false);
    const [showBinModal, setShowBinModal] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(null);
    const [rowError, setRowError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);

    const records = useMemo(() => data?.records || [], [data]);
    const kpis = data?.kpis || {};

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        const f = q
            ? records.filter(r => LEGAL_COLS.some(c => String(r[c.key] || '').toLowerCase().includes(q)))
            : records;
        return [...f].sort((a, b) => {
            const result = String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
            return sortDir * result;
        });
    }, [records, search, sortField, sortDir]);

    useEffect(() => { setPage(1); }, [search, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / LEGAL_PAGE_SIZE));
    const paginated = filtered.slice((page - 1) * LEGAL_PAGE_SIZE, page * LEGAL_PAGE_SIZE);

    const toggleSort = f => {
        if (sortField === f) setSortDir(d => -d);
        else { setSortField(f); setSortDir(1); }
    };

    const exportCSV = () => {
        const cols = [...LEGAL_COLS, { key: 'current_status_comments', label: 'Comments' }, { key: 'additional_comments', label: 'Additional Comments' }];
        const csvEscape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const rows = [
            cols.map(c => csvEscape(c.label)).join(','),
            ...filtered.map(r => cols.map(c => csvEscape(r[c.key])).join(',')),
        ];
        const url = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' }));
        Object.assign(document.createElement('a'), { href: url, download: 'legal_records.csv' }).click();
        URL.revokeObjectURL(url);
    };

    // Delete no longer happens on click — it opens ConfirmModal first; the
    // actual save({ delete_id }) call (which the backend turns into a move to
    // LegalRecordBin, not a hard delete — see legal_views/bin.py) only runs
    // once the user picks "Yes" there.
    const handleDelete = (record) => setConfirmDelete(record);

    const confirmDeleteRecord = async () => {
        const record = confirmDelete;
        if (!record) return;
        setDeleteBusy(record.id); setRowError('');
        try {
            await save({ delete_id: record.id });
            setConfirmDelete(null);
        } catch (err) {
            setRowError(err.message);
        } finally {
            setDeleteBusy(null);
        }
    };

    const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
        .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
            acc.push(p);
            return acc;
        }, []);

    if (!canManageData) {
        return <div className="pl-empty"><p>You do not have permission to manage Legal data.</p></div>;
    }

    return (
        <>
            {showImportModal && (
                <LegalImportModal
                    uploading={uploading}
                    onClose={() => { if (!uploading) setShowImportModal(false); }}
                    onProceed={(file, mode) => { setShowImportModal(false); upload(file, mode); }}
                />
            )}
            {showAddModal && (
                <LegalRecordModal records={records} save={save} onClose={() => setShowAddModal(false)} onSaved={() => setShowAddModal(false)} />
            )}
            {editRecord && (
                <LegalRecordModal record={editRecord} records={records} save={save} onClose={() => setEditRecord(null)} onSaved={() => setEditRecord(null)} />
            )}
            {timelineRecord && (
                <LegalTimelineModal
                    recordId={timelineRecord.id} entityName={timelineRecord.entity_name}
                    canManageData={canManageData}
                    onClose={() => setTimelineRecord(null)}
                    onChanged={onStatusChanged}
                />
            )}

            <div className="legal-kpi-grid">
                <div className="legal-kcard legal-kc-total"><div className="legal-kcard-label">Total Records</div><div className="legal-kcard-val">{kpis.total ?? 0}</div></div>
                <div className="legal-kcard legal-kc-completed"><div className="legal-kcard-label">Completed</div><div className="legal-kcard-val">{kpis.completed ?? 0}</div></div>
                <div className="legal-kcard legal-kc-progress"><div className="legal-kcard-label">In Progress</div><div className="legal-kcard-val">{kpis.in_progress ?? 0}</div></div>
                <div className="legal-kcard legal-kc-hold"><div className="legal-kcard-label">On Hold</div><div className="legal-kcard-val">{kpis.on_hold ?? 0}</div></div>
                <div className="legal-kcard legal-kc-awaited"><div className="legal-kcard-label">Signed Copy Awaited</div><div className="legal-kcard-val">{kpis.signed_copy_awaited ?? 0}</div></div>
                <div className="legal-kcard legal-kc-cancelled"><div className="legal-kcard-label">Cancelled</div><div className="legal-kcard-val">{kpis.cancelled ?? 0}</div></div>
            </div>

            <div className="bm-wrap">
                <div className="bm-toolbar">
                    <div className="bm-toolbar-left">
                        {/* <span className="bm-title">Manage Legal</span> */}
                        {/* <span className="bm-badge">{filtered.length} records</span> */}
                    </div>
                    <input className="bm-search" placeholder="Search legal records…" value={search} onChange={e => setSearch(e.target.value)} />
                    <div className="legal-status-legend" title="What each status color means">
                        {STATUS_LEGEND.map(({ label, className }) => (
                            <span className="legal-status-legend-item" key={label}>
                                <span className={`legal-status-chip ${className}`}>{label}</span>
                            </span>
                        ))}
                    </div>
                    <div className="bm-toolbar-right">
                        <button className="bm-export-btn" title="Download the filtered table as CSV" onClick={exportCSV}>↓ Export CSV</button>
                        <button className="bm-export-btn" onClick={() => setShowAddModal(true)}>+ Add Record</button>
                        <button className={`bm-import-btn${uploading ? ' bm-import-loading' : ''}`} disabled={uploading} onClick={() => setShowImportModal(true)}>
                            {uploading ? 'Importing…' : '↑ Import Legal Excel'}
                        </button>
                        <button className="bm-export-btn" onClick={() => setShowCategoriesModal(true)}>🗂️ Manage Categories</button>
                        <button className="bm-export-btn" title="Let an external system push records in as JSON" onClick={() => setShowApiModal(true)}>🔌 API</button>
                        <button className="bm-export-btn" title="View deleted records" onClick={() => setShowBinModal(true)}>🗑️ Bin</button>
                        <button className="legal-action-btn legal-expand-btn" title="Expand to full-screen table" onClick={() => setShowExpanded(true)}>⛶</button>
                    </div>
                </div>
                {rowError && (
                    <div style={{ padding: '8px 16px', fontSize: 12.5, color: '#b91c1c', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>{rowError}</div>
                )}

                <div className="bm-table-wrap legal-table-wrap">
                    <LegalTable
                        rows={paginated}
                        offset={(page - 1) * LEGAL_PAGE_SIZE}
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={toggleSort}
                        onEdit={setEditRecord}
                        onDelete={handleDelete}
                        onTimeline={setTimelineRecord}
                        deleteBusy={deleteBusy}
                    />
                </div>

                <div className="bm-pagination">
                    <span className="bm-pg-info">
                        {filtered.length === 0 ? '0' : `${(page - 1) * LEGAL_PAGE_SIZE + 1}–${Math.min(page * LEGAL_PAGE_SIZE, filtered.length)}`} of {filtered.length}
                    </span>
                    <div className="bm-pg-controls">
                        <button className="bm-pg-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                        <button className="bm-pg-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
                        {pageNums.map((p, i) =>
                            p === '…'
                                ? <span key={`el-${i}`} className="bm-pg-ellipsis">…</span>
                                : <button key={p} className={`bm-pg-btn${p === page ? ' bm-pg-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                        )}
                        <button className="bm-pg-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</button>
                        <button className="bm-pg-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                    </div>
                </div>
            </div>

            {showExpanded && (
                <LegalExpandedTable
                    rows={filtered}
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={toggleSort}
                    onEdit={setEditRecord}
                    onDelete={handleDelete}
                    onTimeline={setTimelineRecord}
                    deleteBusy={deleteBusy}
                    onClose={() => setShowExpanded(false)}
                    search={search}
                    onSearchChange={setSearch}
                    onExport={exportCSV}
                    onAdd={() => setShowAddModal(true)}
                    onImport={() => setShowImportModal(true)}
                    onManageCategories={() => setShowCategoriesModal(true)}
                    onManageApi={() => setShowApiModal(true)}
                    onManageBin={() => setShowBinModal(true)}
                    uploading={uploading}
                />
            )}

            {confirmDelete && (
                <ConfirmModal
                    icon="🗑️"
                    title="Delete Record?"
                    message={`Delete the record for "${confirmDelete.entity_name || 'this entry'}"? It will be moved to the Bin, where it can be restored or permanently deleted later.`}
                    confirmLabel="Yes, Delete"
                    danger
                    busy={deleteBusy === confirmDelete.id}
                    onConfirm={confirmDeleteRecord}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}

            {showBinModal && (
                <LegalBinModal
                    onClose={() => setShowBinModal(false)}
                    onRestored={onStatusChanged}
                />
            )}

            {showCategoriesModal && (
                <ManageCategoriesModal
                    canManageData={canManageData}
                    onClose={() => setShowCategoriesModal(false)}
                    onChanged={onCategoriesChanged}
                />
            )}

            {showApiModal && (
                <ManageLegalApiModal
                    canManageData={canManageData}
                    onClose={() => setShowApiModal(false)}
                />
            )}
        </>
    );
}

export { ManageLegal };
export default ManageLegal;
